import type { ReactNode } from 'react'
import { cn } from '../../utils/cn'

export interface ChipProps {
  children: ReactNode
  /** Selectable chip: renders aria-pressed and the selected fill. */
  selected?: boolean
  onClick?: () => void
  /** Removable chip: adds a trailing x. Can be combined with onClick. */
  onRemove?: () => void
  disabled?: boolean
  /** Material Symbols glyph name shown before the label. */
  icon?: string
  className?: string
}

/**
 * One chip covers both jobs the design needs: the services filter row (toggle)
 * and the slot picker (selectable tile). Disabled slots keep the Stitch
 * treatment — struck through and faded rather than hidden.
 */
export function Chip({ children, selected = false, onClick, onRemove, disabled, icon, className }: ChipProps) {
  const interactive = Boolean(onClick) && !disabled

  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-full border font-body-md text-body-md transition-colors duration-300',
        onRemove ? 'py-2 pl-4 pr-2' : 'px-4 py-2',
        selected
          ? 'border-primary bg-primary text-on-primary'
          : 'border-outline-variant bg-surface-container-lowest text-on-surface',
        interactive && !selected && 'hover:border-primary hover:text-primary',
        disabled && 'cursor-not-allowed opacity-50 line-through',
        className,
      )}
    >
      {interactive || onClick ? (
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-pressed={selected}
          className="inline-flex items-center gap-2 rounded-full outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary disabled:cursor-not-allowed"
        >
          {icon && <span className="material-symbols-outlined text-[18px]">{icon}</span>}
          {children}
        </button>
      ) : (
        <span className="inline-flex items-center gap-2">
          {icon && <span className="material-symbols-outlined text-[18px]">{icon}</span>}
          {children}
        </span>
      )}

      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label="Remove"
          className={cn(
            'inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors',
            selected ? 'hover:bg-on-primary/20' : 'hover:bg-primary/10',
          )}
        >
          <span className="material-symbols-outlined text-[16px]">close</span>
        </button>
      )}
    </span>
  )
}
